export function sortedType<T>(type: Record<string, T & { ordinal: number, name: string }>): (T & { name: string })[] {
  return Object.values(type)
    .sort((a, b) => a.ordinal - b.ordinal)
}

export const sortedValues = <T>(type: Map<string, T & { ordinal: number }>): T [] =>
  [...type.values()].sort((a, b) => a.ordinal - b.ordinal);

export function addName<T extends Record<string, any>>(type: T): { [K in keyof T]: T[K] & { name: string } } {
  Object.keys(type).forEach(key => {
    type[key].name = key
  });
  return type as any;
}

export function addNameMap<K, V>(type: Map<K, V>): Map<K, V & { name: K }> {
  const res = new Map<K, V & { name: K }>()
  type.forEach((value, key) => {
    res.set(key, {...value, name: key})
  })
  return res
}

function getKeyById(type, id: string): string {
  const found = Object.entries(type).find(entry => (entry[1] as any)._id == id)
  if (!found) {
    console.error(`Unknown id ${id} for type ${JSON.stringify(type)}`);
    return null
  }
  return found[0]
}

export function getFirePartitionPositionName(pos): FirePartitionPositionName | "" {
  if (!pos) return ""
  return getKeyById(FIRE_PARTITION_POSITIONS, pos._id) as FirePartitionPositionName
}

export function getFirePartitionRequirementsName(req): FirePartitionRequirementsName | "" {
  if (!req) return ""
  return getKeyById(FIRE_PARTITION_REQUIREMENTS, req._id) as FirePartitionRequirementsName
}

export interface FirePartition {
  _id?: string;
  code: string;
  type: FirePartitionTypeName;
  status: FirePartitionStatusName;
  projectId: string;
  zoneId: string;
  mapId: string;
  zone: { name: string, description: string; };
  isExtraWork: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: User;
  qrCode: string;
  floor: string;
  roomNumber: string;
  extra: string;
  position: any;
  positionOtherText: string;
  requirements: any;
  pictures: Picture[];
  materials: Material[];
  coordinateX?: number,
  coordinateY?: number,
  facilityType?: FacilityTypeName;
  brand?: string;
  model?: string;
  identification?: string;
  installationMethod?: InstallationMethodName;
}

export interface User {
  _id: string;
  username: string;
  name: string;
}

export interface Picture {
  _id: string;
  image: string;
}

export interface Material {
  _id: string;
  materialType: {
    _id: string;
    name: string;
    nameDutch: string;
    specificationType: MaterialSpecificationType;
    specifications: MaterialSpecification[],
    forFacilityTypes: FacilityType[] | null;
  },
  materialSpecification: MaterialSpecification | null;
  materialSpecificationText: string | null;
  materialSpecificationBrand: string | null;
  materialRemarks: string | null;
  noOfUnits: number;
  materialStatus: MaterialStatusName;
  materialPlacedDate: string;
  materialMaintenanceDate: string;
  materialMaintenanceIntervalMonths: number;
  pricePerUnit: number;
  createdAt: string;
  updatedAt: string;
}

export type MaterialSpecificationType = 'NONE' | 'LIST' | 'MAINTENANCE'

export interface MaterialSpecification {
  _id: string;
  name: string;
}

export enum FirePartitionTypeName {
  NORMAL = "NORMAL",
  OTHER = "OTHER",
  INSTALLATION_TECHNICAL_FACILITY = "INSTALLATION_TECHNICAL_FACILITY"
}

interface _FirePartitionType {
  ordinal: number;
  _id: string;
  nameDutch: string;
  markerClass: string;
}

export interface FirePartitionType extends _FirePartitionType {
  name: FirePartitionTypeName;
}

export const FIRE_PARTITION_TYPES = addNameMap(new Map<FirePartitionTypeName, _FirePartitionType>([
  [FirePartitionTypeName.NORMAL, {
    ordinal: 0,
    _id: "6048923ede650c06488e24d7",
    nameDutch: "Afdichting",
    markerClass: "marker-pin-normal",
  }],
  [FirePartitionTypeName.OTHER, {
    ordinal: 1,
    _id: "6048926e4576cdea6212b057",
    nameDutch: "Overig",
    markerClass: "marker-pin-other",
  }],
  [FirePartitionTypeName.INSTALLATION_TECHNICAL_FACILITY, {
    ordinal: 2,
    _id: "7048926e4576cdea6212c168",
    nameDutch: "Installatietechnische voorziening",
    markerClass: "marker-pin-installation-technical-facility",
  }],
]))

export enum FirePartitionStatusName {
  NORMAL = "NORMAL",
  UNKNOWN = "UNKNOWN",
  BROKEN = "BROKEN",
  BAD_INPUT = "BAD_INPUT",
  THIRD_PARTY = "THIRD_PARTY",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export enum MaterialStatusName {
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

interface _FirePartitionStatus {
  ordinal: number;
  _id: string;
  nameDutch: string;
  forTypes: FirePartitionTypeName[];
  level: string;
}

export interface FirePartitionStatus extends _FirePartitionStatus {
  name: FirePartitionStatusName;
}

export const FIRE_PARTITION_STATUSES = addNameMap(new Map<FirePartitionStatusName, _FirePartitionStatus>([
  [FirePartitionStatusName.NORMAL, {
    ordinal: 0,
    _id: "602a71884d0641305e823ad1",
    nameDutch: "voldoet",
    forTypes: [FirePartitionTypeName.NORMAL, FirePartitionTypeName.OTHER],
    level: 'ok',
  }],
  [FirePartitionStatusName.UNKNOWN, {
    ordinal: 1,
    _id: "602a71884d0641305e823ad2",
    nameDutch: "voldoet, mits",
    forTypes: [FirePartitionTypeName.NORMAL, FirePartitionTypeName.OTHER],
    level: 'warn',
  }],
  [FirePartitionStatusName.BROKEN, {
    ordinal: 2,
    _id: "602a71884d0641305e823ad3",
    nameDutch: "voldoet niet",
    forTypes: [FirePartitionTypeName.NORMAL, FirePartitionTypeName.OTHER],
    level: 'error',
  }],
  [FirePartitionStatusName.BAD_INPUT, {
    ordinal: 3,
    _id: "602a71884d0641305e823ad4",
    nameDutch: "foutieve invoer",
    forTypes: [FirePartitionTypeName.NORMAL, FirePartitionTypeName.OTHER],
    level: 'wrong',
  }],
  [FirePartitionStatusName.THIRD_PARTY, {
    ordinal: 4,
    _id: "602a71884d0641305e823ad5",
    nameDutch: "derde partij",
    forTypes: [FirePartitionTypeName.NORMAL, FirePartitionTypeName.OTHER],
    level: 'other',
  }],
  [FirePartitionStatusName.APPROVED, {
    ordinal: 5,
    _id: "602a71884d0641305e823ad6",
    nameDutch: "goedgekeurd",
    forTypes: [FirePartitionTypeName.INSTALLATION_TECHNICAL_FACILITY],
    level: 'ok',
  }],
  [FirePartitionStatusName.REJECTED, {
    ordinal: 6,
    _id: "602a71884d0641305e823ad7",
    nameDutch: "afgekeurd",
    forTypes: [FirePartitionTypeName.INSTALLATION_TECHNICAL_FACILITY],
    level: 'error',
  }],
]))

export const MATERIAL_STATUSES = addNameMap(new Map<MaterialStatusName, _MaterialStatus>([
  [MaterialStatusName.APPROVED, {
    ordinal: 1,
    nameDutch: "Goedgekeurd",
    firePartitionStatus: FirePartitionStatusName.APPROVED,
  }],
  [MaterialStatusName.REJECTED, {
    ordinal: 2,
    nameDutch: "Afgekeurd",
    firePartitionStatus: FirePartitionStatusName.REJECTED,
  }],
]))

interface _MaterialStatus {
  nameDutch: string;
  ordinal: number;
  firePartitionStatus: FirePartitionStatusName;
}

export interface MaterialStatus extends _MaterialStatus {
  name: MaterialStatusName;
}

export enum FacilityTypeName {
  ESCAPE_ROUTE_LIGHTING = 'ESCAPE_ROUTE_LIGHTING',
  ESCAPE_ROUTE_INDICATION = 'ESCAPE_ROUTE_INDICATION',
  FIRE_HOSE_REEL = 'FIRE_HOSE_REEL',
  PORTABLE_FIRE_EXTINGUISHER = 'PORTABLE_FIRE_EXTINGUISHER',
  BHV_FACILITY = 'BHV_FACILITY',
  FIRE_SAFETY_SYSTEM = 'FIRE_SAFETY_SYSTEM',
  DRY_RISER = 'DRY_RISER',
}

interface _FacilityType {
  nameDutch: string;
  ordinal: number;
}

export interface FacilityType extends _FacilityType {
  name: FacilityTypeName;
}

export const FACILITY_TYPES = addNameMap(new Map<FacilityTypeName, _FacilityType>([
  [FacilityTypeName.ESCAPE_ROUTE_LIGHTING, {
    ordinal: 0,
    nameDutch: "Vluchtrouteverlichting",
  }],
  [FacilityTypeName.ESCAPE_ROUTE_INDICATION, {
    ordinal: 1,
    nameDutch: "Vluchtrouteaanduiding",
  }],
  [FacilityTypeName.FIRE_HOSE_REEL, {
    ordinal: 2,
    nameDutch: "Brandslanghaspel",
  }],
  [FacilityTypeName.PORTABLE_FIRE_EXTINGUISHER, {
    ordinal: 3,
    nameDutch: "Draagbaar blustoestel",
  }],
  [FacilityTypeName.BHV_FACILITY, {
    ordinal: 4,
    nameDutch: "BHV Voorziening",
  }],
  [FacilityTypeName.FIRE_SAFETY_SYSTEM, {
    ordinal: 5,
    nameDutch: "Detectie / ontruiming / sturing",
  }],
  [FacilityTypeName.DRY_RISER, {
    ordinal: 6,
    nameDutch: "Droge blusleiding",
  }],
]))

export enum InstallationMethodName {
  RECESSED_MOUNTED = 'RECESSED_MOUNTED',
  SURFACE_MOUNTED = 'SURFACE_MOUNTED',
}

interface _InstallationMethodType {
  nameDutch: string;
  ordinal: number;
}

export interface InstallationMethodType extends _InstallationMethodType {
  name: InstallationMethodName;
}

export const INSTALLATION_METHOD_TYPES = addNameMap(new Map<InstallationMethodName, _InstallationMethodType>([
  [InstallationMethodName.RECESSED_MOUNTED, {
    ordinal: 0,
    nameDutch: "Inbouw",
  }],
  [InstallationMethodName.SURFACE_MOUNTED, {
    ordinal: 1,
    nameDutch: "Opbouw",
  }],
]))

export enum FirePartitionPositionName {
  FLOOR = 'FLOOR',
  WALL = 'WALL',
  CEILING = 'CEILING',
  WALL_CEILING = 'WALL_CEILING',
  WALL_FLOOR = 'WALL_FLOOR',
  OTHERS = 'OTHERS'
}

export const FIRE_PARTITION_POSITIONS = addName({
  CEILING: {
    ordinal: 0,
    _id: '604894631580c59dc2e21000',
    nameDutch: "plafond"
  },
  WALL: {
    ordinal: 1,
    _id: "6048944df18db48621a77e37",
    nameDutch: "wand"
  },
  FLOOR: {
    ordinal: 2,
    _id: "60489429a38019277eccce52",
    nameDutch: "vloer"
  },
  WALL_CEILING: {
    ordinal: 3,
    _id: "60d1b0c2dde46af8bb8c18d6",
    nameDutch: "wand/plafond"
  },
  WALL_FLOOR:
    {
      ordinal: 4,
      _id: "60d1b117f2255ddb763dbfd0",
      nameDutch: "wand/vloer"
    },
  OTHERS: {
    ordinal: 5,
    _id: "6083a166ae15d6ea8f383253",
    nameDutch: "diversen"
  }
})

export enum FirePartitionRequirementsName {
  ZERO = 'ZERO',
  THIRTY = 'THIRTY',
  SIXTY = 'SIXTY',
  NINETY = 'NINETY',
  HUNDREDTWENTY = 'HUNDREDTWENTY',
  TWOHUNDREDFORTY = 'TWOHUNDREDFORTY',
  ROOK = 'ROOK'
}

export const FIRE_PARTITION_REQUIREMENTS = addName({
  ZERO: {
    _id: "604889ccacaa176b6620575a",
    ordinal: 0,
    value: "0"
  },
  THIRTY: {
    _id: "60488a08d0326f77391510a9",
    ordinal: 1,
    value: "30"
  },
  SIXTY: {
    _id: "60488a259a1884626cccb8f4",
    ordinal: 2,
    value: "60"
  },
  NINETY: {
    _id: "60488a4280b8258d722eec0e",
    ordinal: 3,
    value: "90"
  },
  HUNDREDTWENTY: {
    _id: "60488a521b68bad4ec93ea9b",
    ordinal: 4,
    value: "120"
  },
  TWOHUNDREDFORTY: {
    _id: "62ce8c73f80f2e24f05281e9",
    ordinal: 5,
    value: "240"
  },
  ROOK: {
    _id: "60488a8b96996ef4691f45b6",
    ordinal: 6,
    value: "rook"
  }
})

export class EditFirePartition {
  _id: string

  mapId: string
  zoneId: string

  type: FirePartitionTypeName
  isExtraWork: boolean
  qrCode?: string
  floor?: string
  roomNumber?: string
  extra?: string
  activeState: string
  code: string
  coordinateX: number = 0.0
  coordinateY: number = 0.0
  requirementsName: FirePartitionRequirementsName | ""
  positionName: FirePartitionPositionName | ""
  status: FirePartitionStatusName
  positionOtherText?: string
  facilityType: FacilityTypeName | ""
  brand?: string;
  model?: string;
  identification?: string;
  installationMethod?: InstallationMethodName | "";
}

export class EditFirePartitionWithImages {
  constructor(
    public readonly firePartition: EditFirePartition,
    public readonly images: ImageInfo[]
  ) {
  }
}

export class ImageInfo {
  constructor(
    public readonly url: string,
    public readonly name: any,
    public readonly image?: File
  ) {
  }
}

export interface MaterialTypesModel {
  _id: string;
  name: string;
  nameDutch: string;
  specificationType: MaterialSpecificationType;
  specifications: MaterialSpecificationData[];
  forFacilityTypes: FacilityTypeName[] | null;
}

export class MaterialSpecificationData {
  _id: string;
  name: string;
}

